import { useEffect } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { theme, ALL_MODELS } from './constants';

const useThreeEngine = ({
    canvasRef,
    animationFrameRef,
    controlsRef,
    cameraTargetPos,
    controlsTargetLookAt,
    isAutoMoving,
    currentFilmOffset,
    targetFilmOffset,
    fadeRegistry,
    updateLinesRef,
    setLoadProgress,
    setLoadStatus,
    setDeviceParts,
    setModelLibrary,
    setScene,
    setCamera,
    setRenderer
}) => {
    useEffect(() => {
        let isMounted = true;
        let currentRenderer = null;
        const canvas = canvasRef.current;

        const initThreeJS = async () => {
            if (!canvas) return;

            const newScene = new THREE.Scene();
            newScene.background = new THREE.Color(theme.bg);
            const newCamera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
            newCamera.position.set(6, 0.5, 1.9);

            const newRenderer = new THREE.WebGLRenderer({
                canvas, antialias: true, powerPreference: "high-performance",
                preserveDrawingBuffer: true, precision: "mediump"
            });
            currentRenderer = newRenderer;
            newRenderer.setSize(window.innerWidth, window.innerHeight);
            newRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));

            newScene.add(new THREE.AmbientLight(0xffffff, 0.8));
            const directional = new THREE.DirectionalLight(0xffffff, 1);
            directional.position.set(5, 10, 7.5);
            newScene.add(directional);

            const controls = new OrbitControls(newCamera, newRenderer.domElement);
            controls.enableDamping = true;
            controlsRef.current = controls;
            controls.addEventListener('start', () => { isAutoMoving.current = false; });

            const manager = new THREE.LoadingManager();
            manager.onProgress = (u, l, t) => setLoadProgress(Math.round((l / t) * 100));
            manager.onLoad = () => { if (isMounted) setLoadStatus('Ready!'); };

            const gltfLoader = new GLTFLoader(manager);
            const textureLoader = new THREE.TextureLoader();
            const matcap = textureLoader.load(`${process.env.PUBLIC_URL}/toon-matcap2.png`);

            const loadModel = (path) => new Promise(resolve => gltfLoader.load(path, gltf => {
                gltf.scene.traverse(c => {
                    if (c.isMesh) {
                        c.material = new THREE.MeshMatcapMaterial({ matcap, color: c.material.color, transparent: true });
                        c.userData.targetOpacity = 1; fadeRegistry.current.push(c);
                    }
                });
                resolve(gltf.scene);
            }, undefined, () => resolve(null)));

            const [top, basicTop, bottomHole, topHole, bottom] = await Promise.all([
                loadModel(`${process.env.PUBLIC_URL}/models/top.glb`),
                loadModel(`${process.env.PUBLIC_URL}/models/basic_top.glb`),
                loadModel(`${process.env.PUBLIC_URL}/models/bottom_hole.glb`),
                loadModel(`${process.env.PUBLIC_URL}/models/top_hole.glb`),
                loadModel(`${process.env.PUBLIC_URL}/models/bottom_2.glb`)
            ]);

            if (!isMounted) return;
            if (top) newScene.add(top);
            if (basicTop) {
                basicTop.traverse(c => {
                    if (c.isMesh) {
                        c.material.opacity = 0;
                        c.userData.targetOpacity = 0;
                    }
                });
                newScene.add(basicTop);
            }
            if (bottomHole) newScene.add(bottomHole);
            if (topHole) newScene.add(topHole);
            if (bottom) newScene.add(bottom);

            setDeviceParts({ top, basicTop, bottomHole, topHole, bottom });

            const library = {};
            for (const part of ALL_MODELS) {
                if (!isMounted) return;
                const model = await loadModel(`${process.env.PUBLIC_URL}/models/${part}.glb`);
                if (model) library[part] = model;
            }
            library['basic_top'] = basicTop;

            setModelLibrary(library); setScene(newScene); setCamera(newCamera); setRenderer(newRenderer);

            const animate = () => {
                if (!isMounted) return;
                animationFrameRef.current = requestAnimationFrame(animate);

                currentFilmOffset.current = THREE.MathUtils.lerp(
                    currentFilmOffset.current, targetFilmOffset.current, 0.05
                );
                newCamera.filmOffset = currentFilmOffset.current;
                newCamera.updateProjectionMatrix();

                if (isAutoMoving.current) {
                    newCamera.position.lerp(cameraTargetPos.current, 0.05);
                    controls.target.lerp(controlsTargetLookAt.current, 0.05);
                    if (newCamera.position.distanceTo(cameraTargetPos.current) < 0.01) isAutoMoving.current = false;
                }

                fadeRegistry.current.forEach(obj => {
                    if (!obj.material) return;
                    const cur = obj.material.opacity, tar = obj.userData.targetOpacity;
                    // Only skip the lerp calculation if truly at target
                    if (Math.abs(cur - tar) > 0.001) {
                        obj.material.opacity += (tar - cur) * 0.05;
                    } else {
                        // Snap to target to avoid drifting
                        obj.material.opacity = tar;
                    }

                    obj.visible = obj.material.opacity > 0.001;
                    obj.material.depthWrite = obj.material.opacity > 0.9;
                });

                controls.update();
                newRenderer.render(newScene, newCamera);
                if (updateLinesRef.current) updateLinesRef.current(newCamera);
            };
            animate();
        };

        initThreeJS();
        return () => {
            isMounted = false;
            cancelAnimationFrame(animationFrameRef.current);
            if (currentRenderer) currentRenderer.dispose();
        };
    }, [canvasRef, animationFrameRef, controlsRef, cameraTargetPos,
        controlsTargetLookAt, isAutoMoving, currentFilmOffset, targetFilmOffset,
        fadeRegistry, updateLinesRef, setLoadProgress, setLoadStatus,
        setDeviceParts, setModelLibrary, setScene, setCamera, setRenderer]); // Only runs once on mount
};

export default useThreeEngine;